import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ErrorReportingService } from '../../services/error-reporting/error-reporting.service';
import { RealEstateLocationSuggestion, RealEstateLocationsService } from '../../services/real-estate-locations/real-estate-locations.service';

@Component({
  selector: 'app-real-estate-search-input',
  standalone: true,
  templateUrl: './real-estate-search-input.component.html',
  styleUrl: './real-estate-search-input.component.scss',
})
export class RealEstateSearchInputComponent implements OnChanges, OnDestroy {
  private static nextId = 0;

  @Input() query = '';
  @Output() inputClick = new EventEmitter<void>();
  @Output() queryChange = new EventEmitter<string>();
  @Output() suggestionChange = new EventEmitter<RealEstateLocationSuggestion | null>();
  @Output() submit = new EventEmitter<RealEstateLocationSuggestion>();
  protected suggestions: RealEstateLocationSuggestion[] = [];
  protected open = false;
  protected activeIndex = -1;
  protected readonly listId = `real-estate-location-suggestions-${RealEstateSearchInputComponent.nextId++}`;
  protected selectedSuggestion: RealEstateLocationSuggestion | null = null;
  private timer?: ReturnType<typeof setTimeout>;
  private requestId = 0;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private errorReportingService: ErrorReportingService,
    private locationsService: RealEstateLocationsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['query'] && this.selectedSuggestion?.label !== this.query) {
      this.selectedSuggestion = null;
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.timer);
    this.requestId++;
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }

  protected onBlur(): void {
    this.open = false;
  }

  protected onInput(event: Event): void {
    this.query = (event.target as HTMLInputElement).value;
    this.queryChange.emit(this.query);
    this.selectedSuggestion = null;
    this.suggestionChange.emit(null);
    this.loadSuggestions();
  }

  protected onFocus(): void {
    if (this.suggestions.length) {
      this.open = true;
    } else if (this.query.trim()) {
      this.loadSuggestions();
    }
  }

  protected clearQuery(): void {
    this.query = '';
    this.queryChange.emit(this.query);
    this.selectedSuggestion = null;
    this.suggestionChange.emit(null);
    this.loadSuggestions();
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      clearTimeout(this.timer);
      this.requestId++;
      this.open = false;
    } else if (event.key === 'ArrowDown' && this.suggestions.length) {
      event.preventDefault();
      this.open = true;
      this.activeIndex = Math.min(this.activeIndex + 1, this.suggestions.length - 1);
    } else if (event.key === 'ArrowUp' && this.open) {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    } else if (event.key === 'Enter') {
      if (this.open && this.activeIndex >= 0) {
        event.preventDefault();
        this.select(this.suggestions[this.activeIndex]);
      } else if (this.selectedSuggestion) {
        event.preventDefault();
        this.search();
      }
    }
  }

  protected search(): void {
    if (this.selectedSuggestion) {
      this.submit.emit(this.selectedSuggestion);
    }
  }

  protected select(suggestion: RealEstateLocationSuggestion): void {
    clearTimeout(this.timer);
    this.requestId++;
    this.query = suggestion.label;
    this.queryChange.emit(this.query);
    this.selectedSuggestion = suggestion;
    this.open = false;
    this.suggestions = [];
    this.activeIndex = -1;
    this.suggestionChange.emit(suggestion);
  }

  private loadSuggestions(): void {
    this.suggestions = [];
    this.activeIndex = -1;
    this.open = false;
    clearTimeout(this.timer);
    const requestId = ++this.requestId;
    const term = this.query.trim();
    if (!term) {
      return;
    }
    this.timer = setTimeout(async () => {
      try {
        const suggestions = await this.locationsService.search({ limit: 10, term });
        if (requestId === this.requestId) {
          this.suggestions = suggestions;
          this.open = this.elementRef.nativeElement.querySelector('input') === document.activeElement;
        }
      } catch (error) {
        this.errorReportingService.log('RealEstateSearchInputComponent.loadSuggestions()', 'search', error);
        if (requestId === this.requestId) {
          this.suggestions = [];
          this.open = false;
        }
      }
    }, 250);
  }
}
